import { ColDef, IRowNode } from '@ag-grid-community/core';
import { Button, Flex, Input, useDisclosure } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { Grid } from '@/components/ui/grid';
import { Field } from '@/components/ui/field';
import { TemplateModal } from './template-modal';
import { SaveTemplateDto, TemplateDto } from '@/lib/ServerApi';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { TemplatesActionRenderer } from './templates-action-renderer';
import { TemplatesTypeRenderer } from './templates-type-renderer';
import { TemplatesDescriptionRenderer } from './templates-description-renderer';
import { TemplatesLinksRenderer } from './templates-links-renderer';

export const TemplatesPage = () => {
    const data = useLoaderData() as TemplateDto[];
    const [filter, setFilter] = useState('');
    const { open: isTemplateModalOpen, onOpen: onTemplateModalOpen, onClose: onTemplateModalClose } = useDisclosure();
    const { revalidate } = useRevalidator();

    const { mutate: saveTemplate } = useMutation<unknown, unknown, SaveTemplateDto>({
        mutationFn: async (body) => {
            return api.template.save(body);
        },
        onSettled: () => {
            revalidate();
        },
    });

    const [colDefs] = useState<ColDef<any | { actions: any }>[]>([
        {
            width: 60,
            suppressHeaderFilterButton: true,
            resizable: false,
            cellRenderer: TemplatesActionRenderer,
        },
        {
            field: 'templateType',
            headerName: 'Type',
            width: 200,
            resizable: true,
            cellRenderer: TemplatesTypeRenderer,
        },
        {
            field: 'description',
            headerName: 'Title / Description',
            flex: 2,
            suppressHeaderFilterButton: false,
            resizable: true,
            wrapText: true,
            cellRenderer: TemplatesDescriptionRenderer,
        },
        {
            field: 'links',
            headerName: 'Attach',
            flex: 2,
            resizable: false,
            suppressHeaderFilterButton: false,
            cellRenderer: TemplatesLinksRenderer,
        },
    ]);

    const doesExternalFilterPass = useCallback(
        (node: IRowNode<TemplateDto>): boolean => {
            if (filter) {
                const foundInLinks = node.data?.links.find((l) => l.description.toLowerCase().includes(filter.toLowerCase()));
                if (foundInLinks) {
                    return true;
                }

                for (const key of ['templateType', 'description']) {
                    const foundInTypeOrDescription = (node.data?.[key as keyof TemplateDto] as string)
                        ?.toLowerCase()
                        .includes(filter.toLowerCase());
                    if (foundInTypeOrDescription) {
                        return true;
                    }
                }

                return false;
            }
            return true;
        },
        [filter],
    );

    const isExternalFilterPresent = useCallback((): boolean => {
        return !!filter;
    }, [filter]);

    return (
        <>
            <Flex h="40px" alignItems="center" justifyContent="space-between" pb="12px">
                <Field flex="0 1 320px">
                    <Input
                        type="search"
                        placeholder="Search"
                        value={filter}
                        onInput={(event) => {
                            setFilter((event.target as HTMLInputElement).value);
                        }}
                    />
                </Field>
                <Flex>
                    <Button onClick={onTemplateModalOpen} as="span" textDecoration="none">
                        Add Template
                    </Button>
                </Flex>
            </Flex>
            <Flex flexDirection="column" style={{ width: '100%', height: 'calc(100% - 40px)' }}>
                <Grid
                    rowSelection="single"
                    suppressRowHoverHighlight
                    suppressCellFocus
                    columnDefs={colDefs}
                    rowData={data}
                    rowHeight={70}
                    doesExternalFilterPass={doesExternalFilterPass}
                    isExternalFilterPresent={isExternalFilterPresent}
                    suppressRowClickSelection
                />
            </Flex>
            {isTemplateModalOpen && (
                <TemplateModal
                    onClose={() => {
                        onTemplateModalClose();
                    }}
                    onAdd={(e) => {
                        saveTemplate(e);
                        onTemplateModalClose();
                    }}
                />
            )}
        </>
    );
};
